import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface UsageStatsRow {
  stat_date: string
  new_users: number
  new_threads: number
  new_comments: number
  new_groups: number
  new_articles: number
  article_reads: number
  link_follows: number
}

export interface AdminUsageStatsResult {
  rows: UsageStatsRow[]
  totalUsers: number
  totalThreads: number
  totalComments: number
  totalGroups: number
  totalArticles: number
  totalArticleReads: number
  totalLinkFollows: number
  fromDate: string
  toDate: string
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

export function useAdminUsageStats(fromDate: Date, toDate: Date) {
  const fromStr = toISODate(fromDate)
  const toStr = toISODate(toDate)

  return useQuery({
    queryKey: ['admin-usage-stats', fromStr, toStr],
    queryFn: async (): Promise<AdminUsageStatsResult> => {
      const { data, error } = await supabase.rpc('get_admin_usage_stats', {
        p_from_date: fromStr,
        p_to_date: toStr,
      })
      if (error) throw error
      const rows = (Array.isArray(data) ? data : []) as Array<{
        stat_date: string
        new_users: number | string
        new_threads: number | string
        new_comments: number | string
        new_groups?: number | string
        new_articles?: number | string
        article_reads?: number | string
        link_follows?: number | string
      }>
      const normalized: UsageStatsRow[] = rows.map((r) => ({
        stat_date: r.stat_date,
        new_users: Number(r.new_users ?? 0),
        new_threads: Number(r.new_threads ?? 0),
        new_comments: Number(r.new_comments ?? 0),
        new_groups: Number(r.new_groups ?? 0),
        new_articles: Number(r.new_articles ?? 0),
        article_reads: Number(r.article_reads ?? 0),
        link_follows: Number(r.link_follows ?? 0),
      }))
      const totalUsers = normalized.reduce((s, r) => s + r.new_users, 0)
      const totalThreads = normalized.reduce((s, r) => s + r.new_threads, 0)
      const totalComments = normalized.reduce((s, r) => s + r.new_comments, 0)
      const totalGroups = normalized.reduce((s, r) => s + r.new_groups, 0)
      const totalArticles = normalized.reduce((s, r) => s + r.new_articles, 0)
      const totalArticleReads = normalized.reduce((s, r) => s + r.article_reads, 0)
      const totalLinkFollows = normalized.reduce((s, r) => s + r.link_follows, 0)
      return {
        rows: normalized,
        totalUsers,
        totalThreads,
        totalComments,
        totalGroups,
        totalArticles,
        totalArticleReads,
        totalLinkFollows,
        fromDate: fromStr,
        toDate: toStr,
      }
    },
    staleTime: 60_000,
  })
}
